import React from 'react';
import { useNavigate } from 'react-router-dom';

interface ReleaseNoteItem {
  version: string;
  date: string;
  tag?: string;
  summary: string;
  details: string[];
}

const RELEASE_NOTES_DATA: ReleaseNoteItem[] = [
  {
    version: "v2.4.2",
    date: "August 17, 2026",
    tag: "Latest",
    summary: "Steel Coil Assessment & Lashing module release, Three.js 3D & SVG visualization, multi-page PDF audit reports, and unified header navigation.",
    details: [
      "Introduced dedicated Steel Coil Assessment & Lashing module compliant with CTU Code 2014 & IMO cargo securing standards.",
      "Added real-time 3D Three.js interactive scene and 2D SVG live technical views for steel coil bedding, stoppers, and lashings.",
      "Engineered multi-page PDF report generator producing isometric technical diagrams and complete calculation trace.",
      "Unified header FileText navigation button across all application pages to route directly to /surveys (Field Reports & Archive).",
      "Synchronized system-wide version stamp to v2.4.2 across API, application UI, PDF footers, and package metadata."
    ]
  },
  {
    version: "v2.4.1",
    date: "July 29, 2026",
    summary: "Documentation, dark-mode readability, language-specific guidance, and PDF reporting refinements.",
    details: [
      "Rebuilt the Turkish and English user manuals with complete operational workflows and real, configured application screenshots.",
      "Added dedicated English screenshots for Optimizer, Calculator, Loop Lashing, wooden dunnage, vessel stoppers, CoG edge cases, Breakbulk validation, results, PDF reports, profile, and field surveys.",
      "Improved dark-mode contrast, dropdown readability, visual hierarchy, and color consistency across calculation and result interfaces.",
      "Localized the saved-PDF archive and PDF viewer controls so English and Turkish sessions display the correct interface language.",
      "Added the v2.4.1 version stamp to generated PDF report footers and synchronized application, API, manual, and package metadata."
    ]
  },
  {
    version: "v2.4.0",
    date: "July 2026",
    summary: "Asia-Pacific maritime expansion with complete Korean localization support.",
    details: [
      "Full application localization for South Korean operations (Calculator, Profile, Portal, Hub).",
      "Korean Unicode character support inside the automated PDF audit certificate engine.",
      "Optimized multi-language dictionary exports for surveyors and third-party auditors.",
      "Validated physics calculation engine compliance against international maritime securing standards."
    ]
  },
  {
    version: "v2.3.0",
    date: "April 2026",
    summary: "Enterprise compliance, data protection standards, and multi-lingual architecture.",
    details: [
      "Integrated KVKK / GDPR compliance documentation, SLA structures, and Data Protection Addendums (DPA).",
      "Added multi-national locales including German (DE), Spanish (ES), Chinese (ZH), and Japanese (JA).",
      "Implemented a secure cookie consent banner and centralized database consent logging."
    ]
  },
  {
    version: "v2.2.0",
    date: "February 2026",
    summary: "Core physics engine upgrade and dynamic 3D digital twin visualization.",
    details: [
      "Calculation methods aligned with international maritime securing and land transportation standards.",
      "Support for 11 transport acceleration modes (Road, Rail, Sea A/B/C, and combined intermodal corridors).",
      "Real-time 3D Center of Gravity (CoG) vector rendering with React Three Fiber.",
      "Dynamic friction coefficient tables matching CTU Code guidelines (ASM, Wood, Metal)."
    ]
  },
  {
    version: "v2.1.0",
    date: "November 2025",
    summary: "Metal stopper force integration and custom branding packs.",
    details: [
      "Added transverse and longitudinal metal stopper blocking calculations.",
      "Introduced custom branding tools, allowing users to upload corporate logos for official PDF certificates.",
      "centralized subscription tiers and secure customer payment portals."
    ]
  },
  {
    version: "v2.0.0",
    date: "June 2025",
    summary: "Initial global SaaS production launch of Lashing Calculator.",
    details: [
      "Launch of the core lashing force optimization and manual calculation engine.",
      "Basic loop and direct lashing calculation configurations.",
      "Print-ready PDF report builder containing official survey stamps and calculation logs."
    ]
  }
];

export const ReleaseNotes: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B0F19] via-[#111827] to-[#0D1527] text-white flex flex-col font-sans">
      {/* Header */}
      <header className="border-b border-white/5 bg-[#0B0F19]/80 backdrop-blur-md sticky top-0 z-50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/resized-buckles-son.png" alt="LashingPlan Logo" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold tracking-wider bg-clip-text text-transparent bg-gradient-to-r from-teal-400 to-cyan-400">LashingPlan</span>
          </div>
          <button 
            onClick={() => navigate('/login')}
            className="px-5 py-2 text-sm font-semibold rounded-full border border-teal-500/30 text-teal-400 hover:bg-teal-500/10 transition-all duration-300 shadow-lg shadow-teal-500/5"
          >
            Access Calculator
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-16 px-6 text-center">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(20,184,166,0.08),transparent_70%)] pointer-events-none"></div>
        <div className="max-w-4xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-white/10 bg-white/5 text-sm text-gray-300 hover:text-white hover:bg-white/10 hover:border-white/20 transition-all duration-300"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back
          </button>
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-teal-500/20 bg-teal-500/5 text-xs text-teal-400 font-semibold mb-6">
              <span className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-ping"></span>
              COMPLIANCE AUDIT RECORD
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-gray-300">
            System Release Notes & Compliance
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            Review detailed updates, standard changes, and calculation engine versions. Essential for validating simulation histories and audit logs.
          </p>
        </div>
      </section>

      {/* Timeline Section */}
      <main className="flex-grow max-w-5xl mx-auto w-full px-6 pb-24">
        <div className="relative border-l border-white/5 pl-8 md:pl-12 space-y-16">
          {RELEASE_NOTES_DATA.map((item, idx) => (
            <div key={item.version} className="relative group">
              {/* Timeline dot */}
              <div className={`absolute -left-[41px] md:-left-[57px] top-1.5 w-6 h-6 rounded-full border-4 border-[#0B0F19] ${item.tag ? 'bg-teal-400 shadow-lg shadow-teal-400/40' : 'bg-gray-700'} transition-all duration-300 group-hover:scale-110`}></div>
              
              {/* Card Container */}
              <div className="rounded-2xl border border-white/5 bg-[#131B2E]/40 p-6 md:p-8 backdrop-blur-md shadow-xl hover:border-teal-500/20 transition-all duration-300">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-4">
                    <span className="text-2xl font-bold tracking-tight text-white">{item.version}</span>
                    {item.tag && (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-teal-500/20 text-teal-300 border border-teal-500/30">
                        {item.tag}
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-semibold text-gray-500">{item.date}</span>
                </div>

                <p className="text-base text-gray-300 font-medium mb-6">{item.summary}</p>

                <h4 className="text-xs font-bold text-gray-500 tracking-wider uppercase mb-3">Key Changes & Compliance Details</h4>
                <ul className="space-y-3">
                  {item.details.map((detail, index) => (
                    <li key={index} className="flex items-start gap-3 text-sm text-gray-400">
                      <span className="text-teal-400 mt-1">✔</span>
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#080B13]/90 py-12 px-6">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-3">
            <img src="/resized-buckles-son.png" alt="LashingPlan Logo" className="w-6 h-6 object-contain opacity-60" />
            <span>© 2026 LashingPlan. All rights reserved.</span>
          </div>
          <div className="flex items-center gap-6">
            <span className="hover:text-gray-300 cursor-pointer" onClick={() => navigate('/privacy')}>Privacy Policy</span>
            <span className="hover:text-gray-300 cursor-pointer" onClick={() => navigate('/terms')}>Terms of Service</span>
            <span className="hover:text-gray-300 cursor-pointer" onClick={() => navigate('/')}>Home</span>
          </div>
        </div>
      </footer>
    </div>
  );
};
export default ReleaseNotes;
